import { Inbox, Loader2, Phone, RefreshCw, MapPin } from "lucide-react";

const isNoTitle = (value = "") => /no title|sin título/i.test(value);

const LeadCard = ({ lead, index }) => (
    <div
        data-testid={`lead-card-${index}`}
        className="rounded-2xl border border-white/10 bg-surface p-5 transition-colors hover:border-volt/30"
    >
        <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
                <h3 className="font-display text-lg font-extrabold text-white">{lead.name}</h3>
                <p className="mt-1 font-display text-base font-bold text-volt">
                    {lead.year} {lead.make} {lead.model}
                </p>
            </div>
            <a
                href={`tel:${String(lead.phone).replace(/[^\d+]/g, "")}`}
                data-testid={`lead-call-${index}`}
                className="inline-flex items-center gap-2 rounded-xl bg-volt px-4 py-2.5 text-xs font-extrabold text-obsidian transition-all hover:bg-volt-dark active:scale-[0.98]"
            >
                <Phone className="h-3.5 w-3.5" />
                {lead.phone}
            </a>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
            <span className="rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold text-slate-300">
                {lead.condition}
            </span>
            <span
                className={`rounded-full border px-3 py-1 text-[11px] font-bold ${
                    isNoTitle(lead.title_status)
                        ? "border-blaze/50 bg-blaze/10 text-blaze"
                        : "border-white/10 bg-white/5 text-slate-300"
                }`}
            >
                {lead.title_status}
            </span>
            {lead.zip_code ? (
                <span className="inline-flex items-center gap-1 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-[11px] font-bold text-slate-300">
                    <MapPin className="h-3 w-3 text-volt" />
                    {lead.zip_code}
                </span>
            ) : null}
        </div>

        {lead.issues?.length ? (
            <div className="mt-2.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold text-slate-500">
                {lead.issues.map((issue) => (
                    <span key={issue}>{issue}</span>
                ))}
            </div>
        ) : null}

        <p className="mt-3 text-[11px] font-semibold text-slate-600">
            {lead.created_at ? lead.created_at.slice(0, 16).replace("T", " ") + " UTC" : ""}
        </p>
    </div>
);

export const LeadsList = ({ leads, loading, error, onRetry, errorText, retryText, emptyText }) => {
    if (loading) {
        return (
            <div data-testid="leads-loading" className="grid place-items-center rounded-3xl border border-white/10 bg-surface py-20">
                <span className="flex items-center gap-3 text-sm font-bold text-slate-400">
                    <Loader2 className="h-5 w-5 animate-spin text-volt" />
                    ...
                </span>
            </div>
        );
    }

    if (error) {
        return (
            <div data-testid="leads-error" className="grid place-items-center rounded-3xl border border-blaze/40 bg-blaze/10 py-16 text-center">
                <p className="text-sm font-bold text-blaze">{errorText}</p>
                <button
                    type="button"
                    onClick={onRetry}
                    data-testid="leads-retry-btn"
                    className="mt-4 inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2 text-xs font-bold text-white transition-colors hover:border-volt/50 hover:text-volt"
                >
                    <RefreshCw className="h-3.5 w-3.5" />
                    {retryText}
                </button>
            </div>
        );
    }

    if (!leads.length) {
        return (
            <div data-testid="leads-empty" className="grid place-items-center rounded-3xl border border-white/10 bg-surface py-20 px-6 text-center">
                <Inbox className="h-10 w-10 text-slate-600" />
                <p className="mt-4 max-w-sm text-sm font-semibold leading-relaxed text-slate-500">{emptyText}</p>
            </div>
        );
    }

    return (
        <div className="space-y-3">
            {leads.map((lead, i) => (
                <LeadCard key={lead.id || i} lead={lead} index={i} />
            ))}
        </div>
    );
};

export default LeadsList;
